import React, { useLayoutEffect, useRef } from "react";
import { Menu } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { FileIcon } from "icons/document/FileIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { formatPaymentListDate } from "utils/DateFormat";
import PaginationComponent from "./PaginationComponent";
import { DocumentAttachment } from "types/Documents";
import { SPINNER_CLASS, MENU_ITEMS } from "constants/documents";
import { useTid } from "utils/testid";

interface MobileDocumentsTableProps {
  data: DocumentAttachment[];
  isTableLoading: boolean;
  isDownloading: string | null;
  timezone?: string;
  totalCount: number;
  perPage: number;
  currentPage: number;
  onPreview: (data: DocumentAttachment) => void;
  onDownload: (path: string, fileName: string, id: string) => void;
  onNextClick: () => void;
  onPreviousClick: () => void;
}

const MobileDocumentsTable: React.FC<MobileDocumentsTableProps> = ({
  data,
  isTableLoading,
  isDownloading,
  timezone = "UTC",
  totalCount,
  perPage,
  currentPage,
  onPreview,
  onDownload,
  onNextClick,
  onPreviousClick,
}) => {
  const tid = useTid();
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const previousPageRef = useRef(currentPage);
  const getDocumentRowKey = (row: DocumentAttachment) =>
    row.id || row.title || row.newTitle || "unknown";

  useLayoutEffect(() => {
    if (previousPageRef.current === currentPage) return;

    previousPageRef.current = currentPage;
    tableBodyRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);

  return (
    <div
      className="w-full px-5 h-full"
      data-testid={tid("mobile-table-section")}
      aria-label="Documents mobile table"
    >
      <div className="w-full h-full flex flex-col">
        {/* Table Header */}
        <div
          className="flex border-b py-3.5 shrink-0"
          data-testid={tid("mobile-header")}
        >
          <div className="pl-5 w-[59%] text-base font-medium text-txtParagraph text-start border-none">
            Name
          </div>
          <div className="w-[34.5%] text-base font-medium text-txtParagraph text-start border-none">
            Created Date
          </div>
          <div className="pr-5 w-[5%] text-base font-medium text-txtParagraph text-start border-none"></div>
        </div>

        {/* Table Body */}
        <div
          ref={tableBodyRef}
          className="flex-1 min-h-0 overflow-y-scroll w-full overflow-x-clip"
        >
          {isTableLoading ? (
            <div
              className="w-full flex flex-col items-start justify-center"
              data-testid={tid("mobile-loading")}
              aria-label="Loading documents"
            >
              <div className="pl-5 flex gap-3 justify-start w-full items-center">
                <div className="w-[56%]">
                  <Skeleton count={20} className="w-full h-[30px] my-[25px]" />
                </div>
                <div className="w-[36%]">
                  <Skeleton count={20} className="w-full h-[30px] my-[25px]" />
                </div>
                <div className="w-[8%]">
                  <Skeleton count={20} className="w-full h-[30px] my-[25px]" />
                </div>
              </div>
            </div>
          ) : (
            data?.map((row) => {
              const rowKey = getDocumentRowKey(row);
              return (
                <div
                  className="py-[25px] w-full flex items-center justify-center hover:bg-bgThird cursor-pointer"
                  key={rowKey}
                  data-testid={tid(`mobile-row-${rowKey}`)}
                  aria-label={`${row.newTitle || "Document"} document row`}
                  onClick={() => onPreview(row)}
                >
                  {/* Name Column */}
                  <div className="pl-5 text-sm text-txtSecondary w-[60%]">
                    <div className="flex gap-2 items-center pr-2">
                      <FileIcon aria-hidden="true" />
                      {row.newTitle}
                    </div>
                  </div>

                  {/* Date Column */}
                  <div className="text-sm text-txtSecondary w-[35%]">
                    <div className="py-[2px]">
                      {row.created_date
                        ? formatPaymentListDate(
                            row.created_date,
                            timezone,
                            "short"
                          )
                        : "--"}
                    </div>
                  </div>

                  {/* Actions Column */}
                  <div className="w-[5%]">
                    <Menu
                      as="div"
                      className="relative flex items-center pr-[10px]"
                    >
                      <Menu.Button
                        onClick={(e) => e.stopPropagation()}
                        data-testid={tid(`mobile-row-${rowKey}-actions`)}
                        aria-label={`Open actions for ${row.newTitle || "document"}`}
                      >
                        <div className="hover:!bg-bgHover rounded-full cursor-pointer">
                          <MoreIcon
                            aria-hidden="true"
                            className="group"
                            _className="group-hover:!fill-iconObscure"
                          />
                        </div>
                      </Menu.Button>

                      <Menu.Items
                        className={`absolute w-[153px] right-0 top-[32px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
                      >
                        <Menu.Item
                          as="div"
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px] cursor-pointer"
                          data-testid={tid(`mobile-row-${rowKey}-menu-preview`)}
                          aria-label={`Preview ${row.newTitle || "document"}`}
                          onClick={() => onPreview(row)}
                        >
                          <EyeIcon width="16" height="16" aria-hidden="true" />{" "}
                          {MENU_ITEMS.PREVIEW}
                        </Menu.Item>
                        <Menu.Item
                          as="div"
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] cursor-pointer"
                          data-testid={tid(
                            `mobile-row-${rowKey}-menu-download`
                          )}
                          aria-label={`Download ${row.newTitle || "document"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownload(row.title, row.newTitle, row.id);
                          }}
                        >
                          {isDownloading === row.id ? (
                            <div className={SPINNER_CLASS} aria-hidden="true" />
                          ) : (
                            <DownloadIcon
                              width="16"
                              height="16"
                              aria-hidden="true"
                            />
                          )}
                          {isDownloading === row.id
                            ? MENU_ITEMS.DOWNLOADING
                            : MENU_ITEMS.DOWNLOAD}
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <PaginationComponent
          perPage={perPage}
          currentPage={currentPage}
          totalCount={totalCount}
          isTableLoading={isTableLoading}
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
        />
      </div>
    </div>
  );
};

export default MobileDocumentsTable;
